"use client"

import { useState } from "react"
import { formatDistanceToNow } from "date-fns"
import { Trash2, MapPin, Loader2, Heart, MessageCircle, Send } from "lucide-react"
// import Stream from "@cloudflare/stream-react" // re-enable when video launches

interface PostAuthor {
  id: string
  name: string
  userType: string
  location?: string | null
}

interface Comment {
  id: string
  content: string
  createdAt: string
  author: { id: string; name: string; userType: string }
}

interface Post {
  id: string
  content?: string | null
  mediaType?: string | null
  imageUrl?: string | null
  muxPlaybackId?: string | null
  muxStatus?: string | null
  location?: string | null
  createdAt: string
  author: PostAuthor
  likeCount?: number
  commentCount?: number
  likedByMe?: boolean
}

interface PostCardProps {
  post: Post
  currentUserId?: string
  onDelete?: (postId: string) => void
}

export default function PostCard({ post, currentUserId, onDelete }: PostCardProps) {
  const [deleting, setDeleting] = useState(false)
  const [liked, setLiked] = useState(post.likedByMe ?? false)
  const [likeCount, setLikeCount] = useState(post.likeCount ?? 0)
  const [likeLoading, setLikeLoading] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentsLoaded, setCommentsLoaded] = useState(false)
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0)
  const [commentInput, setCommentInput] = useState("")
  const [submittingComment, setSubmittingComment] = useState(false)
  const [commentsLoading, setCommentsLoading] = useState(false)

  const isOwner = currentUserId === post.author.id

  const initials = post.author.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const handleDelete = async () => {
    if (!confirm("Delete this post?")) return
    setDeleting(true)
    try {
      const res = await fetch(`/api/posts/${post.id}`, { method: "DELETE" })
      if (res.ok) onDelete?.(post.id)
    } catch (e) {
      console.error(e)
    } finally {
      setDeleting(false)
    }
  }

  const handleLike = async () => {
    if (likeLoading) return
    setLikeLoading(true)
    // Optimistic update
    setLiked((prev) => !prev)
    setLikeCount((prev) => (liked ? prev - 1 : prev + 1))
    try {
      const res = await fetch(`/api/posts/${post.id}/like`, { method: "POST" })
      if (res.ok) {
        const data = await res.json()
        setLiked(data.liked)
        setLikeCount(data.count)
      } else {
        // Revert on failure
        setLiked((prev) => !prev)
        setLikeCount((prev) => (liked ? prev + 1 : prev - 1))
      }
    } catch {
      setLiked((prev) => !prev)
      setLikeCount((prev) => (liked ? prev + 1 : prev - 1))
    } finally {
      setLikeLoading(false)
    }
  }

  const loadComments = async () => {
    if (commentsLoaded) return
    setCommentsLoading(true)
    try {
      const res = await fetch(`/api/posts/${post.id}/comments`)
      if (res.ok) {
        const data = await res.json()
        setComments(data.comments)
        setCommentsLoaded(true)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setCommentsLoading(false)
    }
  }

  const handleToggleComments = () => {
    const next = !showComments
    setShowComments(next)
    if (next && !commentsLoaded) loadComments()
  }

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentInput.trim() || submittingComment) return
    setSubmittingComment(true)
    try {
      const res = await fetch(`/api/posts/${post.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: commentInput.trim() }),
      })
      if (res.ok) {
        const data = await res.json()
        setComments((prev) => [...prev, data.comment])
        setCommentCount((prev) => prev + 1)
        setCommentInput("")
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    try {
      const res = await fetch(`/api/posts/${post.id}/comments/${commentId}`, { method: "DELETE" })
      if (res.ok) {
        setComments((prev) => prev.filter((c) => c.id !== commentId))
        setCommentCount((prev) => prev - 1)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow mb-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{post.author.name}</p>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span className="capitalize">{post.author.userType}</span>
              {post.location && (
                <>
                  <span>·</span>
                  <MapPin className="h-3 w-3" />
                  <span>{post.location}</span>
                </>
              )}
              <span>·</span>
              <span>{formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}</span>
            </div>
          </div>
        </div>
        {isOwner && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Text content */}
      {post.content && (
        <p className="px-4 py-2 text-sm text-gray-800 whitespace-pre-wrap">{post.content}</p>
      )}

      {/* Image */}
      {post.mediaType === "image" && post.imageUrl && (
        <div className="mt-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={post.imageUrl} alt="Post image" className="w-full max-h-96 object-cover" />
        </div>
      )}

      {/* Video — disabled until video launch
      {post.mediaType === "video" && (
        <div className="mt-2 bg-black">
          {post.muxStatus === "ready" && post.muxPlaybackId ? (
            <Stream
              src={post.muxPlaybackId}
              controls
              style={{ width: "100%", aspectRatio: "16/9" }}
            />
          ) : post.muxStatus === "errored" ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              Video processing failed
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm space-x-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Video processing…</span>
            </div>
          )}
        </div>
      )}
      */}

      {/* Like & Comment actions */}
      <div className="flex items-center space-x-4 px-4 py-3 border-t border-gray-100 mt-1">
        <button
          onClick={handleLike}
          disabled={likeLoading}
          className="flex items-center space-x-1.5 text-sm transition-colors group"
        >
          <Heart
            className={`h-5 w-5 transition-all ${
              liked ? "fill-red-500 text-red-500" : "text-gray-400 group-hover:text-red-400"
            }`}
          />
          <span className={`${liked ? "text-red-500" : "text-gray-500 group-hover:text-red-400"}`}>
            {likeCount > 0 ? likeCount : ""}
          </span>
        </button>

        <button
          onClick={handleToggleComments}
          className="flex items-center space-x-1.5 text-sm text-gray-400 hover:text-blue-500 transition-colors group"
        >
          <MessageCircle className={`h-5 w-5 ${showComments ? "text-blue-500" : "group-hover:text-blue-400"}`} />
          <span className={`${showComments ? "text-blue-500" : "text-gray-500 group-hover:text-blue-400"}`}>
            {commentCount > 0 ? commentCount : ""}
          </span>
        </button>
      </div>

      {/* Comments section */}
      {showComments && (
        <div className="border-t border-gray-100 px-4 pb-4">
          {commentsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {comments.length > 0 && (
                <div className="space-y-3 pt-3 mb-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex items-start space-x-2 group">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-xs font-semibold flex-shrink-0">
                        {comment.author.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-800">{comment.author.name}</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                            </span>
                            {comment.author.id === currentUserId && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 ml-1 transition-all"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-0.5">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Comment input */}
              <form onSubmit={handleSubmitComment} className="flex items-center space-x-2 pt-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold flex-shrink-0">
                  {currentUserId ? "Y" : "?"}
                </div>
                <div className="flex-1 flex items-center bg-gray-100 rounded-full px-3 py-1.5 space-x-2">
                  <input
                    type="text"
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    placeholder="Write a comment…"
                    className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400 min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={!commentInput.trim() || submittingComment}
                    className="text-blue-500 disabled:text-gray-300 hover:text-blue-700 transition-colors flex-shrink-0"
                  >
                    {submittingComment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  )
}
